import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { markReturn, takeReturn } from "@/lib/drawerReturn";
import { Gem, ChevronDown, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCreator360Access, AV_NAME, AV_SHORT, AVENUES } from "@/lib/creator360";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Creator360Profile } from "@/components/c360/profile/Creator360Profile";

// v81 — one gem signal everywhere: the user's gem map (own verdict changes + demo blur), one request per page view.
const STORE = { email: "", p: null, data: null, subs: new Set() };
const DRAWER = { open: null, subs: new Set() };

function fetchGems(email) {
  STORE.email = email;
  STORE.p = api.get("/creator360/gems").then((r) => {
    const d = { map: new Map(Object.entries(r.data.gems || {})), blur: r.data.blur || null };
    if (!r.data.ready) STORE.p = null;
    STORE.data = d;
    STORE.subs.forEach((fn) => fn(d));
    return d;
  }).catch(() => { STORE.p = null; return null; });
  return STORE.p;
}

export function resetGems() {
  if (STORE.email && STORE.subs.size) fetchGems(STORE.email);
  else { STORE.p = null; STORE.data = null; }
}

export function useGems() {
  const access = useCreator360Access();
  const { user } = useAuth();
  const email = user?.email || "";
  const on = access.enabled && !!email;
  const [data, setData] = useState(() => (on && STORE.email === email ? STORE.data : null));
  useEffect(() => {
    if (!on) { setData(null); return undefined; }
    STORE.subs.add(setData);
    if (STORE.email !== email || !STORE.p) fetchGems(email);
    else STORE.p.then((d) => d && setData(d));
    return () => { STORE.subs.delete(setData); };
  }, [on, email]);
  const map = on ? data?.map || null : null;
  return { on: on && !!map, map, blur: on ? data?.blur || null : null, isGem: (id) => !!(id && map?.has(id)), avs: (id) => map?.get(id) || [] };
}

export const gemHover = (avs) => `Hidden gem · Strong on ${AVENUES.filter((a) => avs.includes(a)).map((a) => AV_NAME[a]).join(", ")} · open in Creator 360`;
export const gemAvsText = (avs) => AVENUES.filter((a) => avs.includes(a)).map((a) => AV_NAME[a]).join(", ");

export function openGem(id) {
  DRAWER.open = { id, view: "best" };
  DRAWER.subs.forEach((fn) => fn(DRAWER.open));
}

export function GemMark({ id, size = 12, testid }) {
  const { map } = useGems();
  const avs = id && map?.get(id);
  if (!avs) return null;
  const t = gemHover(avs);
  return (
    <span role="button" tabIndex={0} title={t} aria-label={t} data-testid={testid || `gem-mark-${id}`}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openGem(id); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); openGem(id); } }}
      className="inline-flex items-center text-cyan-400 hover:text-cyan-200 transition-colors shrink-0 align-middle cursor-pointer"><Gem size={size} /></span>
  );
}

// Mounted once in AppShell: the Creator 360 drawer opened by any gem mark; Close returns to the same page.
// v83 — after "Main profile →" from here, Back to this page reopens the same drawer.
export function GemDrawerHost() {
  const [gem, setGem] = useState(null);
  const loc = useLocation();
  const here = loc.pathname + loc.search;
  useEffect(() => { DRAWER.subs.add(setGem); return () => { DRAWER.subs.delete(setGem); }; }, []);
  useEffect(() => { const back = takeReturn("gem", here); if (back) setGem(back); }, [loc.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = useCallback(() => setGem(null), []);
  const leave = useCallback(() => { markReturn("gem", here, gem); setGem(null); }, [here, gem]);
  if (!gem) return null;
  return createPortal(<Creator360Profile id={gem.id} view={gem.view} from="c360_gem" onView={(v) => setGem((g) => ({ ...g, view: v }))} onClose={close} onLeave={leave} />, document.body);
}

// v82 — one Hidden gems filter everywhere: Strong on ANY picked avenue; no pick = every gem.
export const gemOk = (g, id, avs = []) => {
  const s = id && g.map?.get(id);
  return !!s && (!avs.length || avs.some((a) => s.includes(a)));
};
export const gemChip = (avs = []) => `Hidden gems${avs.length ? ` · ${AVENUES.filter((a) => avs.includes(a)).map((a) => AV_SHORT[a]).join(", ")}` : ""}`;
export const gemParams = (on, avs = []) => (on ? { gem: true, ...(avs.length ? { gem_avs: avs.join(",") } : {}) } : {});

export function GemFilter({ on, avs = [], setOn, setAvs, testid = "gem-only-toggle", className = "" }) {
  const g = useGems();
  if (!g.on) return null;
  const pick = (a) => setAvs(avs.includes(a) ? avs.filter((x) => x !== a) : [...avs, a]);
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`${testid}-group`}>
      <button type="button" data-testid={testid} title={on ? "Turn off Hidden gems only" : "Show only hidden gems"}
        onClick={() => { if (on) setAvs([]); setOn(!on); }}
        className={`text-xs px-3 py-2 rounded-md border inline-flex items-center gap-2 transition-colors ${on ? "border-cyan-400/70 bg-cyan-500/10 text-cyan-200" : "border-zinc-700 text-zinc-300 hover:border-cyan-400/60 hover:text-cyan-300"} ${className}`}
        style={{ background: on ? undefined : "var(--surface)" }}>
        <Gem size={13} /><span data-testid={`${testid}-label`}>{on ? gemChip(avs) : "Hidden gems only"}</span>{on && <span aria-hidden>×</span>}
      </button>
      {on && (
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid={`${testid}-avenues`} className={`text-xs px-2.5 py-2 rounded-md border inline-flex items-center gap-1.5 border-cyan-400/40 text-cyan-200 hover:border-cyan-400/70 transition-colors ${className}`} style={{ background: "var(--surface)" }}>
              Strong on: {avs.length ? `${avs.length} picked` : "any avenue"}<ChevronDown size={12} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-2 border-zinc-700" style={{ background: "var(--surface)" }} data-testid={`${testid}-avenue-menu`}>
            <div className="mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 px-2 py-1">Hidden gem Strong on any of</div>
            {AVENUES.map((a) => (
              <button key={a} type="button" onClick={() => pick(a)} data-testid={`${testid}-av-${a}`}
                className={`w-full text-left text-sm px-2 py-1.5 rounded flex items-center gap-2 transition-colors ${avs.includes(a) ? "text-cyan-200 bg-cyan-500/10" : "text-zinc-300 hover:bg-white/5"}`}>
                <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center ${avs.includes(a) ? "border-cyan-400 bg-cyan-500/30" : "border-zinc-600"}`}>{avs.includes(a) && <Check size={10} />}</span>
                {AV_NAME[a]}
              </button>
            ))}
            <div className="text-[11px] text-zinc-500 px-2 pt-1.5">No pick = every hidden gem</div>
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
}



// v79 API kept for Creator Intel / Unaffiliated Intel.
export function useGemDrawer() {
  const mark = (id) => <GemMark id={id} />;
  return { mark, drawer: null };
}
